import { ErrorNotice, Notice } from '../../components/ui/Notice';
import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { Transition } from '../../api/types';
import type { DragError } from './useCardDrag';

/**
 * 🔴 WHAT A 409 LOOKS LIKE ON SCREEN.
 *
 * ⭐⭐ PH-19 DID NOT MAKE THIS FILE LESS NECESSARY. The board no longer refuses a drop because the
 * server did not SUGGEST it — but the server still refuses moves, for reasons this client must not
 * duplicate and must not paraphrase: not a member of the project, a retired target status, a ticket
 * already in that status. Those rules live in `services/transitions.ts` and this renders them.
 *
 *   · its sentence, VERBATIM — ⛔ never replaced by "That change was refused", which is what the
 *     generic error path produces and which tells the reader nothing about what to do next;
 *   · the statuses it SUGGESTED, as buttons, so the next action is one click rather than a guess;
 *   · an explicit "do not retry that target", because the one thing a reader does after a failure is
 *     try the same thing again, and here that is guaranteed to fail identically.
 *
 * ⛔⛔ WHAT THIS MUST NOT SAY ANY MORE: that an empty list means the ticket cannot move. It used to,
 * and under advisory transitions that sentence is FALSE — a ticket with no suggested next status can
 * still be dragged anywhere active. The empty case now says what is true: nothing was SUGGESTED.
 *
 * ⭐ The refused target is withdrawn from this drag's drop targets (`refusedStatuses` in
 * `useCardDrag`), so this notice and the board agree — the advice is not merely printed.
 */
export function RefusalNotice({
  failure,
  busy,
  onChoose,
  onDismiss,
}: {
  failure: DragError;
  busy: boolean;
  onChoose: (toStatus: string) => void;
  onDismiss: () => void;
}) {
  const vocab = useVocabulary();
  const { resolved } = useTheme();

  if (failure.error) {
    // Not a refusal — a 401, a 503, a network failure. The shared notice already says the right
    // thing about each of those, and duplicating it here would let the two drift.
    return <ErrorNotice error={failure.error} />;
  }

  const refusal = failure.refusal;
  if (!refusal) return null;

  const attempted = refusal.attemptedStatus ? vocab.status(refusal.attemptedStatus) : null;

  return (
    <Notice
      tone="error"
      title={`${failure.ticket.key} was not moved`}
      action={
        <button
          type="button"
          onClick={onDismiss}
          className="rounded border border-current/30 px-2.5 py-1 text-xs font-semibold"
        >
          Dismiss
        </button>
      }
    >
      {/* The API's own words. Quoted, so it is clear whose sentence this is. */}
      <p className="font-mono text-xs break-words">{refusal.message}</p>

      <p className="mt-1">
        ⛔ Do not retry <strong>{attempted?.label ?? refusal.attemptedStatus ?? 'that target'}</strong>{' '}
        — the API refused it from{' '}
        <strong>{vocab.status(refusal.fromStatus)?.label ?? refusal.fromStatus ?? 'here'}</strong>, and
        it has been withdrawn as a drop target for this drag. Other statuses are unaffected.
      </p>

      {refusal.suggestedWasSent ? (
        refusal.suggested.length > 0 ? (
          <div className="mt-2">
            {/* ⚠️ "suggests", not "says it can go to". Any active status is a legal target; these are
                the likely ones, and wording them as the permitted set is the error PH-19 removed. */}
            <p className="text-xs font-semibold">The API suggests, from here:</p>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {refusal.suggested.map((entry) => (
                <li key={entry.to_status}>
                  <TargetButton
                    transition={entry}
                    busy={busy}
                    isDark={resolved === 'dark'}
                    label={entry.label ?? vocab.status(entry.to_status)?.label ?? entry.to_status}
                    onChoose={() => onChoose(entry.to_status)}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          /* ⛔ It used to say "this ticket cannot move until an admin adds a transition in Settings",
             which is now FALSE and sends the reader to configure something that decides nothing. */
          <p className="mt-2 text-xs">
            The API suggested <strong>no</strong> next status from here. That is not a refusal to
            move — any other active status is still a valid drop.
          </p>
        )
      ) : (
        // ⚠️ Absent, not empty. Saying "it suggested none" would be a claim the response did not make.
        <p className="mt-2 text-xs">
          The refusal carried no suggestions. Drop the card on any other column, or open the status
          dropdown on the card to ask for the suggested ones.
        </p>
      )}
    </Notice>
  );
}

function TargetButton({
  transition,
  label,
  busy,
  isDark,
  onChoose,
}: {
  transition: Transition;
  label: string;
  busy: boolean;
  isDark: boolean;
  onChoose: () => void;
}) {
  const colours = chipColours(transition.colour, isDark);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onChoose}
      title={transition.note ?? undefined}
      style={{ backgroundColor: colours.background, color: colours.foreground }}
      className="rounded-[3px] px-1.5 py-0.5 text-[11px] font-bold tracking-wide uppercase disabled:opacity-60"
    >
      {label}
    </button>
  );
}
